import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { EnvelopeIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import OTPInput from '../../components/Auth/OTPInput';
import LanguageSelector from '../../components/LanguageSelector';
import { apiService } from '../../services/api';
import { validatePassword, PasswordPolicy, PasswordValidationResult } from '../../utils/passwordValidation';

interface ForgotPasswordFormData {
  email: string;
}

interface PasswordResetResponse {
  success: boolean;
  message: string;
  otpSent?: boolean;
}

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation('forgotPassword');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<'email' | 'otp' | 'password' | 'success'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null);
  const [isLoadingPasswordPolicy, setIsLoadingPasswordPolicy] = useState(false);
  const [isExpiredPassword, setIsExpiredPassword] = useState(false);
  
  // Use ref to store verified OTP to avoid state timing issues
  const verifiedOtpRef = useRef<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ForgotPasswordFormData>();

  // Check for email and expired query params on mount
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const expiredParam = searchParams.get('expired');
    
    if (emailParam) {
      setEmail(emailParam);
      setValue('email', emailParam);
      
      // Fetch password policy early if email is pre-filled (e.g., from expired password redirect)
      // This allows users to see requirements before requesting OTP
      const fetchPolicy = async () => {
        try {
          setIsLoadingPasswordPolicy(true);
          const policy = await apiService.getPasswordPolicy(emailParam);
          setPasswordPolicy(policy);
        } catch (error: any) {
          console.error('Failed to fetch password policy:', error);
          // Set default policy as fallback
          setPasswordPolicy({
            min_length: 8,
            require_uppercase: false,
            require_lowercase: false,
            require_numbers: false,
            require_special_chars: false,
            prohibit_easy_to_guess: false,
            prohibit_sequential_numbers: false,
            max_sequential_numbers: 2,
            prohibit_repeated_characters: false,
            max_repeated_characters: 2,
            prohibit_common_passwords: false,
            prohibit_user_info: false,
            require_password_rotation: false,
            password_rotation_days: 90,
            password_expiration_notification_days: 7,
          });
        } finally {
          setIsLoadingPasswordPolicy(false);
        }
      };
      
      fetchPolicy();
    }
    
    if (expiredParam === 'true') {
      setIsExpiredPassword(true);
      toast.error(t('messages.passwordExpired'));
    }
  }, [searchParams, setValue, t]);

  const onSubmitEmail = async (data: ForgotPasswordFormData) => {
    try {
      setIsLoading(true);
      setEmail(data.email);
      
      const response = await fetch('/api/v1/auth/password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: data.email }),
      });

      const result: PasswordResetResponse = await response.json();

      if (response.ok && result.otpSent) {
        setStep('otp');
        toast.success(t('messages.otpSent'));
        startResendCooldown(); // Start 30-second cooldown immediately
      } else {
        toast.error(result.message || t('messages.otpSendFailed'));
      }
    } catch (error: any) {
      toast.error(t('messages.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitOTP = async (enteredOtp: string) => {
    try {
      setIsLoading(true);
      setOtpError('');
      
      const response = await fetch('/api/v1/auth/verify-password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email, 
          otp: enteredOtp 
        }),
      });

      const result: PasswordResetResponse = await response.json();

      if (response.ok && result.success) {
        console.log('Setting OTP to:', enteredOtp); // Debug log
        setOtp(enteredOtp); // Store the verified OTP in state
        verifiedOtpRef.current = enteredOtp; // Also store in ref for immediate access
        setStep('password');
        toast.success(t('messages.otpVerified'));
        
        // Fetch password policy when entering password step
        if (email) {
          try {
            setIsLoadingPasswordPolicy(true);
            const policy = await apiService.getPasswordPolicy(email);
            setPasswordPolicy(policy);
          } catch (error: any) {
            console.error('Failed to fetch password policy:', error);
            // Set default policy as fallback
            setPasswordPolicy({
              min_length: 8,
              require_uppercase: false,
              require_lowercase: false,
              require_numbers: false,
              require_special_chars: false,
              prohibit_easy_to_guess: false,
              prohibit_sequential_numbers: false,
              max_sequential_numbers: 2,
              prohibit_repeated_characters: false,
              max_repeated_characters: 2,
              prohibit_common_passwords: false,
              prohibit_user_info: false,
              require_password_rotation: false,
              password_rotation_days: 90,
              password_expiration_notification_days: 7,
            });
          } finally {
            setIsLoadingPasswordPolicy(false);
          }
        }
      } else {
        setOtpError(result.message || t('messages.otpInvalid'));
        toast.error(t('messages.otpInvalid'));
      }
    } catch (error: any) {
      setOtpError(t('messages.networkError'));
      toast.error(t('messages.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    try {
      setIsLoading(true);
      
      const response = await fetch('/api/v1/auth/password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email }),
      });

      const result: PasswordResetResponse = await response.json();

      if (response.ok && result.otpSent) {
        toast.success(t('messages.otpResent'));
        startResendCooldown();
      } else {
        toast.error(result.message || t('messages.otpResendFailed'));
      }
    } catch (error: any) {
      toast.error(t('messages.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(30);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Validate password in real-time
  useEffect(() => {
    if (password && passwordPolicy) {
      const validation = validatePassword(
        password,
        passwordPolicy,
        email, // Use email for user info check
        undefined, // Name not available in forgot password flow
        t // Pass translation function
      );
      setPasswordValidation(validation);
    } else {
      setPasswordValidation(null);
    }
  }, [password, passwordPolicy, email, t]);

  const onSubmitPassword = async () => {
    // Clear previous errors
    setPasswordError('');
    
    // Basic validation
    if (!password || !confirmPassword) {
      setPasswordError(t('messages.passwordFieldsRequired'));
      return;
    }
    
    if (password !== confirmPassword) {
      setPasswordError(t('messages.passwordsNoMatch'));
      return;
    }

    // Validate password against policy
    if (passwordPolicy) {
      const validation = validatePassword(
        password,
        passwordPolicy,
        email,
        undefined
      );
      
      if (!validation.isValid) {
        setPasswordError(validation.errors[0] || t('messages.passwordRequirementsNotMet'));
        return;
      }
    } else if (password.length < 8) {
      // Fallback validation if policy not loaded
      setPasswordError(t('messages.passwordMinLength'));
      return;
    }
    
    try {
      setIsLoading(true);
      
      console.log('Submitting password reset with OTP from state:', otp); // Debug log
      console.log('Submitting password reset with OTP from ref:', verifiedOtpRef.current); // Debug log
      
      const response = await fetch('/api/v1/auth/complete-password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email, 
          otp: verifiedOtpRef.current || otp, // Use ref first, fallback to state
          new_password: password 
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // 🔒 Auth data is now in httpOnly cookies - no localStorage needed!
        // Backend sets cookies automatically (auth_token, device_id, device_name, session_type)
        
        setStep('success');
        toast.success(t('messages.passwordResetSuccess'));
        // Force page reload to trigger AuthContext re-initialization
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        setPasswordError(result.message || t('messages.passwordResetFailed'));
        toast.error(result.message || t('messages.passwordResetFailed'));
      }
    } catch (error: any) {
      setPasswordError(t('messages.networkError'));
      toast.error(t('messages.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12 relative">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-start mb-8">
          <img 
            src="/logo-gov.svg" 
            alt="Governate Logo" 
            className="h-16"
          />
        </div>

        {/* Back Button */}
        <button
          onClick={handleBackToLogin}
          className="flex items-center text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          {t('backToLogin')}
        </button>

        {/* Card Container */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 shadow-2xl">

          {/* Step 1: Email Input */}
          {step === 'email' && (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">
                  {isExpiredPassword ? t('steps.email.titleExpired') : t('steps.email.title')}
                </h1>
                <p className="text-gray-400">
                  {isExpiredPassword 
                    ? t('steps.email.descriptionExpired')
                    : t('steps.email.description')}
                </p>
                {isExpiredPassword && (
                  <div className="mt-4 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
                    <p className="text-yellow-400 text-sm">
                      {t('steps.email.expiredWarning')}
                    </p>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit(onSubmitEmail)} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('steps.email.emailLabel')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-500" />
                    </div>
                    <input
                      {...register('email', {
                        required: t('steps.email.emailRequired'),
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t('steps.email.emailInvalid'),
                        },
                      })}
                      type="email"
                      className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                      placeholder={t('steps.email.emailPlaceholder')}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-white text-black py-3 px-4 rounded-xl font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isLoading ? t('steps.email.submitting') : t('steps.email.submit')}
                </button>
              </form>
            </>
          )}

          {/* Step 2: OTP Verification */}
          {step === 'otp' && (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">{t('steps.otp.title')}</h1>
                <p className="text-gray-400">
                  {t('steps.otp.description')} <span className="font-semibold text-white">{email}</span>
                </p>
              </div>

              <div className="space-y-6">
                <OTPInput
                  onComplete={onSubmitOTP}
                  disabled={isLoading}
                  error={otpError}
                />

                <div className="text-center">
                  <button
                    onClick={handleResendOTP}
                    disabled={isLoading || resendCooldown > 0}
                    className={`
                      text-sm font-medium transition-all duration-200 px-4 py-2 rounded-md
                      ${resendCooldown > 0 || isLoading
                        ? 'text-gray-600 bg-white/5 cursor-not-allowed opacity-60'
                        : 'text-gray-300 hover:text-white hover:bg-white/10 cursor-pointer'
                      }
                    `}
                  >
                    {isLoading 
                      ? t('steps.otp.resendSending')
                      : resendCooldown > 0 
                        ? t('steps.otp.resendCooldown', { count: resendCooldown })
                        : t('steps.otp.resend')
                    }
                  </button>
                </div>

                <button
                  onClick={() => setStep('email')}
                  className="w-full text-sm text-gray-400 hover:text-white"
                >
                  {t('steps.otp.useDifferentEmail')}
                </button>
              </div>
            </>
          )}

          {/* Step 3: Password Reset Form */}
          {step === 'password' && (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">{t('steps.password.title')}</h1>
                <p className="text-gray-400">
                  {t('steps.password.description')}
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('steps.password.newPasswordLabel')}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all ${
                      password.length > 0 && passwordValidation && !passwordValidation.isValid
                        ? 'border-red-400/50 bg-red-500/10' 
                        : password.length > 0 && passwordValidation && passwordValidation.isValid
                        ? 'border-green-400/50 bg-green-500/10' 
                        : 'border-white/20'
                    }`}
                    placeholder={passwordPolicy ? t('steps.password.newPasswordPlaceholderWithMin', { min: passwordPolicy.min_length }) : t('steps.password.newPasswordPlaceholder')}
                    disabled={isLoading || isLoadingPasswordPolicy}
                  />
                  {isLoadingPasswordPolicy && (
                    <div className="text-xs mt-1 text-gray-400">
                      {t('steps.password.loadingRequirements')}
                    </div>
                  )}
                  {!isLoadingPasswordPolicy && password.length > 0 && passwordValidation && (
                    <div className="mt-2 space-y-1">
                      {/* Show validation checks */}
                      {passwordPolicy && (
                        <>
                          {passwordValidation.checks.minLength.message && (
                            <div className={`text-xs ${passwordValidation.checks.minLength.valid ? 'text-green-400' : 'text-red-400'}`}>
                              {passwordValidation.checks.minLength.message}
                            </div>
                          )}
                          {passwordPolicy.require_uppercase && passwordValidation.checks.uppercase.message && (
                            <div className={`text-xs ${passwordValidation.checks.uppercase.valid ? 'text-green-400' : 'text-red-400'}`}>
                              {passwordValidation.checks.uppercase.message}
                            </div>
                          )}
                          {passwordPolicy.require_lowercase && passwordValidation.checks.lowercase.message && (
                            <div className={`text-xs ${passwordValidation.checks.lowercase.valid ? 'text-green-400' : 'text-red-400'}`}>
                              {passwordValidation.checks.lowercase.message}
                            </div>
                          )}
                          {passwordPolicy.require_numbers && passwordValidation.checks.numbers.message && (
                            <div className={`text-xs ${passwordValidation.checks.numbers.valid ? 'text-green-400' : 'text-red-400'}`}>
                              {passwordValidation.checks.numbers.message}
                            </div>
                          )}
                          {passwordPolicy.require_special_chars && passwordValidation.checks.specialChars.message && (
                            <div className={`text-xs ${passwordValidation.checks.specialChars.valid ? 'text-green-400' : 'text-red-400'}`}>
                              {passwordValidation.checks.specialChars.message}
                            </div>
                          )}
                          {passwordPolicy.prohibit_sequential_numbers && passwordValidation.checks.sequentialNumbers.message && (
                            <div className={`text-xs ${passwordValidation.checks.sequentialNumbers.valid ? 'text-green-400' : 'text-red-400'}`}>
                              {passwordValidation.checks.sequentialNumbers.message}
                            </div>
                          )}
                          {passwordPolicy.prohibit_repeated_characters && passwordValidation.checks.repeatedChars.message && (
                            <div className={`text-xs ${passwordValidation.checks.repeatedChars.valid ? 'text-green-400' : 'text-red-400'}`}>
                              {passwordValidation.checks.repeatedChars.message}
                            </div>
                          )}
                          {passwordPolicy.prohibit_user_info && passwordValidation.checks.userInfo.message && (
                            <div className={`text-xs ${passwordValidation.checks.userInfo.valid ? 'text-green-400' : 'text-red-400'}`}>
                              {passwordValidation.checks.userInfo.message}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('steps.password.confirmPasswordLabel')}
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                    placeholder={t('steps.password.confirmPasswordPlaceholder')}
                    disabled={isLoading}
                  />
                </div>

                {passwordError && (
                  <div className="text-sm text-red-400 text-center">
                    {passwordError}
                  </div>
                )}

                <button
                  onClick={onSubmitPassword}
                  disabled={isLoading || !password || !confirmPassword || (passwordValidation && !passwordValidation.isValid) || isLoadingPasswordPolicy}
                  className="w-full bg-white text-black py-3 px-4 rounded-xl font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isLoading ? t('steps.password.submitting') : isLoadingPasswordPolicy ? t('steps.password.loading') : t('steps.password.submit')}
                </button>

                <button
                  onClick={() => setStep('otp')}
                  className="w-full text-sm text-gray-400 hover:text-white"
                >
                  {t('steps.password.backToOtp')}
                </button>
              </div>
            </>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <CheckCircleIcon className="h-16 w-16 text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">{t('steps.success.title')}</h1>
              <p className="text-gray-400 mb-6">
                {t('steps.success.description')}
              </p>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-600 mt-6">
          {t('footer')}
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
